import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DeviceCard from '../components/devices/DeviceCard';
import AddDeviceModal from '../components/devices/AddDeviceModal';
import Loader from '../components/Loader'; 
import { motion } from 'framer-motion';
import { FaPlus } from 'react-icons/fa';

const Devices = () => {
  const { propertyId } = useParams();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem('token');
  
  const [devices, setDevices] = useState([]);
  const [stances, setStances] = useState([]); // Se cargan solo la primera vez
  const [selectedStance, setSelectedStance] = useState('all');
  const [role, setRole] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true); 

  // Obtener los datos de la propiedad
  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setRole(response.data.role);
    } catch (error) {
      console.error('Error al obtener los detalles de la propiedad:', error);
    }
  };

  // Obtener dispositivos y estancias solo en la primera carga
  const fetchDevicesAndStances = async (stance = 'all', updateStatesOnly = false) => {
    const url =
      stance === 'all'
        ? `${API_BASE_URL}devices/${propertyId}`
        : `${API_BASE_URL}devices/${propertyId}/stances/${stance}`;

    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (updateStatesOnly) {
        // Solo actualizar los estados sin afectar la estructura de la lista
        setDevices((prevDevices) =>
          prevDevices.map((device) => {
            const updatedDevice = response.data.devices.find((d) => d._id === device._id);
            return updatedDevice ? { ...device, state: updatedDevice.state } : device;
          })
        );
      } else {
        // Primera carga completa o cambio de estancia
        setDevices(response.data.devices || []);

        // Guardar las estancias solo si aún no han sido cargadas
        if (stances.length === 0 && response.data.devices) {
          const allStances = [...new Set(response.data.devices.map((d) => d.stance))];
          setStances(allStances);
        }

        setTimeout(() => setIsLoading(false), 500);
        setFirstLoad(false);
      }
    } catch (error) {
      console.error('Error al cargar dispositivos y estancias:', error);
    }
  };

  useEffect(() => {
    fetchProperty();
    fetchDevicesAndStances(selectedStance, false); // Primera carga completa

    const interval = setInterval(() => fetchDevicesAndStances(selectedStance, true), 5000);
    return () => clearInterval(interval);
  }, [API_BASE_URL, propertyId, selectedStance]);

  const handleStanceChange = (stance) => {
    setSelectedStance(stance);
    setIsLoading(true);
    fetchDevicesAndStances(stance, false);
  };

  const handleAddDevice = async (newDevice) => {
    try {
      // Determinar la estancia correcta
      const stance = newDevice.stance === 'other' ? newDevice.customStance.trim() : newDevice.stance;

      if (!stance) {
        alert('Debes seleccionar o escribir una estancia válida.');
        return;
      }

      const formattedDevice = {
        name: newDevice.name,
        propertyId: propertyId,
        type: newDevice.type,
        state: Boolean(newDevice.state), // Asegurar que sea booleano
        ha_id: newDevice.ha_id,
        stance, // Se usa la estancia del modal, no del navbar
      };

      await axios.post(`${API_BASE_URL}devices`, formattedDevice, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setShowModal(false);
      fetchDevicesAndStances(selectedStance, false); // Recargar la lista completa después de agregar
    } catch (error) {
      console.error('Error al añadir dispositivo:', error);
      alert('Hubo un error al añadir el dispositivo.');
    }
  };

  return (
    <StyledDevices>
      {/* Navbar de stances */}
      <StyledNavbar>
        <div className="navbar-links">
          <button
            className={`navbar-item ${selectedStance === 'all' ? 'active' : ''}`}
            onClick={() => handleStanceChange('all')}
          >
            All
          </button>
          {stances.map((stance) => (
            <button
              key={stance}
              className={`navbar-item ${selectedStance === stance ? 'active' : ''}`}
              onClick={() => handleStanceChange(stance)}
            >
              {stance.charAt(0).toUpperCase() + stance.slice(1).replace('_', ' ')}
            </button>
          ))}
        </div>
      </StyledNavbar>

      <motion.h1
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        Dispositivos
      </motion.h1>

      {/* Botón de agregar dispositivos */}
      {(role === 'admin' || role === 'owner') && (
        <motion.button
          className="add-btn"
          onClick={() => setShowModal(true)}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FaPlus className="icon" />
        </motion.button>
      )}

      {/* Loader centrado mientras se cargan los dispositivos */}
      {isLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        <div className="container">
          <div className="devices-grid">
            {devices.map((device) => (
              <DeviceCard
                key={device._id}
                id={device._id}
                name={device.name}
                type={device.type}
                state={device.state || 'off'}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modal para añadir dispositivos */}
      {showModal && (
        <AddDeviceModal
          propertyId={propertyId}
          stances={stances} // Asegúrate de que las estancias se pasen como prop
          onClose={() => setShowModal(false)}
          onAddDevice={handleAddDevice}
        />
      )}
    </StyledDevices>
  );
};

// Loader completamente centrado
const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh; /* Ajustado para centrar en la pantalla */
  width: 100%;
`;

// Navbar minimalista
const StyledNavbar = styled.nav`
  display: flex;
  justify-content: center;
  padding: 10px 0;
  margin-bottom: 20px;

  .navbar-links {
    display: flex;
    gap: 10px;
  }

  .navbar-item {
    background: none;
    color: #333;
    border: none;
    padding: 10px 15px;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s ease, color 0.3s ease;

    &.active {
      background-color: #008080;
      color: white;
    }

    &:hover {
      background-color: #f0f0f0;
    }
  }
`;

// Contenedor principal
const StyledDevices = styled.div`
  padding: 20px;

  h1 {
    margin-bottom: 20px;
    font-size: 2rem;
    text-align: center;
  }

  .add-btn {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 60px;
    height: 60px;
    background-color: #008080;
    color: white;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    cursor: pointer;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 4px 10px;
    transition: all 0.3s ease;
  }

  .add-btn:hover {
    background-color: rgb(22, 104, 106);
    transform: scale(1.1);
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 15px;
  }

  .devices-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
`;

export default Devices;